import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";

/**
 * @class NonNachaParticipants
 * @classdesc Class definition for record type from model
 * @param {OBJECT} record - record from the model to init class
 * @extends BCMSRecord
 * @example const participant = new NonNachaParticipants(model.nonNachaParticipants[0]);
 *
 * @property {NUMBER} _id - The id of the non NACHA participant
 * @property {STRING} _participantType - The type of the non NACHA participant
 * @property {STRING} _participantName - The name of the non NACHA participant
 * @property {STRING} _participantEmail - The email address of the non NACHA participant
 * @property {STRING} _nickname - The nickname of the non NACHA participant
 * @property {STRING} _institutionName - The institutionName of the non NACHA participant
 * @property {NUMBER} _uniqueID - The uniqueID of the non NACHA participant
 * @property {bcmsDate} _createdDate - The createdDate of the non NACHA participant
 * @property {STRING} _group - The group of the non NACHA participant
 * @property {STRING} _number - The number of the non NACHA participant
 * @property {STRING} _routing - The routing of the non NACHA participant
 * @property {STRING} _accountType - The accountType of the non NACHA participant
 * @property {STRING} _status - The status of the non NACHA participant
 * @property {STRING} _discretionaryData - The discretionaryData of the non NACHA participant
 * @property {BOOLEAN} _distributePayments - The distributePayments of the non NACHA participant
 * @property {STRING} _isChecked - Component functionality specific property.
 */
export default class NonNachaParticipants extends BCMSRecord {
  _id;
  _participantType;
  _participantName;
  _participantEmail;
  _nickname;
  _institutionName;
  _uniqueID;
  _createdDate;
  _group;
  _fullNumber;
  _number;
  _confirmedAccountNumber;
  _routing;
  _transactionCode;
  _accountType;
  _status;
  _discretionaryData;
  _distributePayments;
  _isChecked;
  _isSelected;
  _isShownInSearch;

  constructor(record) {
    super(record);
  }
}

NonNachaParticipants.define({
  recordType: "nonNachaParticipants",
  recordIDProp: "id",
  className: "NonNachaParticipants",
  recordProperties: [
    /**
     * @id
     * @instance
     * @property get {NUMBER} - gets the id of the non NACHA participant
     * @property set {NUMBER} - sets the id of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToNumber("id", "_id"),

    /**
     * @participantType
     * @instance
     * @property get {STRING} - gets the type of the non NACHA participant
     * @property set {STRING} - sets the type of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("participantType", "_participantType"),

    /**
     * @participantName
     * @instance
     * @property get {STRING} - gets the name of the non NACHA participant
     * @property set {STRING} - sets the name of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("participantName", "_participantName"),

    /**
     * @participantEmail
     * @instance
     * @property get {STRING} - gets the email of the non NACHA participant
     * @property set {STRING} - sets the email of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("participantEmail", "_participantEmail"),

    /**
     * @nickname
     * @instance
     * @property get {STRING} - gets the nickname of the non NACHA participant
     * @property set {STRING} - sets the nickname of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("nickname", "_nickname"),

    /**
     * @institutionName
     * @instance
     * @property get {STRING} - gets the institutionName of the non NACHA participant
     * @property set {STRING} - sets the institutionName of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("institutionName", "_institutionName"),

    /**
     * @uniqueID
     * @instance
     * @property get {STRING} - gets the uniqueID of the non NACHA participant
     * @property set {STRING} - sets the uniqueID of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("uniqueID", "_uniqueID"),

    /**
     * @createdDate
     * @instance
     * @property get {bcmsDate} - gets the createdDate of the non NACHA participant
     * @property set {bcmsDate} - sets the createdDate of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToDate("createdDate", "_createdDate"),

    /**
     * @group
     * @instance
     * @property get {STRING} - gets the group of the non NACHA participant
     * @property set {STRING} - sets the group of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("group", "_group"),
    /**
     * @fullNumber
     * @instance
     * @property get {STRING} - gets the full number of the non NACHA participant
     * @property set {STRING} - sets the full number of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("fullNumber", "_fullNumber"),
    /**
     * @number
     * @instance
     * @property get {STRING} - gets the number of the non NACHA participant
     * @property set {STRING} - sets the number of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("number", "_number"),

    /**
     * @number
     * @instance
     * @property get {STRING} - gets the confirmedAccountNumber of the non NACHA participant
     * @property set {STRING} - sets the confirmedAccountNumber of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("confirmedAccountNumber", "_confirmedAccountNumber"),

    /**
     * @routing
     * @instance
     * @property get {STRING} - gets the routing of the non NACHA participant
     * @property set {STRING} - sets the routing of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("routing", "_routing"),
    /**
     * @transactionCode
     * @instance
     * @property get {STRING} - gets the transaction code of the non NACHA participant
     * @property set {STRING} - sets the transaction code  of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("transactionCode", "_transactionCode"),

    /**
     * @accountType
     * @instance
     * @property get {STRING} - gets the accountType of the non NACHA participant
     * @property set {STRING} - sets the accountType of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("accountType", "_accountType"),

    /**
     * @status
     * @instance
     * @property get {STRING} - gets the status of the non NACHA participant
     * @property set {STRING} - sets the status of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("status", "_status"),

    /**
     * @discretionaryData
     * @instance
     * @property get {STRING} - gets the discretionaryData of the non NACHA participant
     * @property set {STRING} - sets the discretionaryData of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("discretionaryData", "_discretionaryData"),

    /**
     * @distributePayments
     * @instance
     * @property get {BOOLEAN} - gets the distributePayments of the non NACHA participant
     * @property set {BOOLEAN} - sets the distributePayments of the non NACHA participant
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToBoolean("distributePayments", "_distributePayments"),

    /**
     * @isChecked
     * @instance
     * @property get {STRING} - gets the checked property of a participant in a component row
     * @property set {STRING} - sets the checked property of a participant in a component row
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToBoolean("isChecked", "_isChecked"),

    /**
     * @createdTime
     * @instance
     * @property get {STRING} - gets the createdTime property of a participant in a component row
     * @property set {STRING} - sets the createdTime property of a participant in a component row
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("createdTime", "_createdTime"),

    /**
     * @createdTime
     * @instance
     * @property get {STRING} - gets the createdTime property of a participant in a component row
     * @property set {STRING} - sets the createdTime property of a participant in a component row
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("isSelected", "_isSelected"),

    /**
     * @isChecked
     * @instance
     * @property get {STRING} - gets the checked property of a participant in a component row
     * @property set {STRING} - sets the checked property of a participant in a component row
     * @memberof NonNachaParticipants
     */
    BCMSRecordProperty.mapToString("isShownInSearch", "_isShownInSearch"),
  ],
});
